import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, ShoppingBag, ShoppingCart, Sparkles, X } from "lucide-react";

const ACCENT = "#FF6A00";

type Step =
  | { kind: "greeting"; text: string }
  | { kind: "suggestions"; chips: string[] }
  | { kind: "user"; text: string }
  | { kind: "typing" }
  | { kind: "bot"; text: string; highlight?: boolean }
  | {
      kind: "quickAdd";
      slot: string;
      name: string;
      price: string;
      added: boolean;
    }
  | { kind: "confirmation"; name: string; price: string }
  | { kind: "finalizeButton" };

// Mirrors the real AiWaiter.tsx flow including the auto-upsell triggered
// when the user clicks "Adicionar" with the chat open.
// Sequence: greeting → suggestion chips → user picks "qual o mais vendido?" →
// typing → bot recommends Burger → quick-add chip → user "clicks" Adicionar →
// confirmation bubble → typing → bot UPSELLS Batata Rústica → quick-add chip →
// user "clicks" → confirmation → user "pode finalizar" → typing →
// "toque no botão abaixo" → Finalize button slides up.
const STEPS: { step: Step; delay: number }[] = [
  { step: { kind: "greeting", text: "Boa noite! Vai pedir algo gostoso? 😋" }, delay: 1100 },
  {
    step: {
      kind: "suggestions",
      chips: ["O que vocês recomendam?", "Tem algo leve?", "Qual o mais vendido?"],
    },
    delay: 1900,
  },
  { step: { kind: "user", text: "Qual o mais vendido?" }, delay: 1000 },
  { step: { kind: "typing" }, delay: 800 },
  {
    step: {
      kind: "bot",
      text: "O Burger Artesanal é o nosso mais pedido, R$ 32,90 🍔",
    },
    delay: 1600,
  },
  {
    step: { kind: "quickAdd", slot: "burger", name: "Burger Artesanal", price: "R$ 32,90", added: false },
    delay: 1200,
  },
  {
    step: { kind: "quickAdd", slot: "burger", name: "Burger Artesanal", price: "R$ 32,90", added: true },
    delay: 900,
  },
  {
    step: { kind: "confirmation", name: "Burger Artesanal", price: "R$ 32,90" },
    delay: 1400,
  },
  { step: { kind: "typing" }, delay: 800 },
  {
    step: {
      kind: "bot",
      text: "Que tal uma Batata Rústica (R$ 14,90) pra acompanhar? Combina muito com o burger 🍟",
      highlight: true,
    },
    delay: 1800,
  },
  {
    step: { kind: "quickAdd", slot: "batata", name: "Batata Rústica", price: "R$ 14,90", added: false },
    delay: 1100,
  },
  {
    step: { kind: "quickAdd", slot: "batata", name: "Batata Rústica", price: "R$ 14,90", added: true },
    delay: 900,
  },
  {
    step: { kind: "confirmation", name: "Batata Rústica", price: "R$ 14,90" },
    delay: 1300,
  },
  { step: { kind: "user", text: "Pode finalizar 🙏" }, delay: 1100 },
  { step: { kind: "typing" }, delay: 800 },
  {
    step: { kind: "bot", text: "Ótimo! Toque no botão abaixo para concluir seu pedido 🛒" },
    delay: 1500,
  },
  { step: { kind: "finalizeButton" }, delay: 2800 },
];

const LOOP_PAUSE_MS = 1800;

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-1 py-0.5">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: ACCENT }}
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

function BotAvatar() {
  return (
    <div
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full mt-0.5"
      style={{ backgroundColor: `${ACCENT}18` }}
    >
      <Bot className="h-3.5 w-3.5" style={{ color: ACCENT }} />
    </div>
  );
}

export function AiWaiterChatDemo() {
  const [visible, setVisible] = useState<number>(1);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const advance = (index: number) => {
      if (cancelled) return;
      const { delay } = STEPS[index];
      timeoutId = setTimeout(() => {
        if (cancelled) return;
        const nextIndex = index + 1;
        if (nextIndex >= STEPS.length) {
          timeoutId = setTimeout(() => {
            if (cancelled) return;
            setVisible(1);
            advance(0);
          }, LOOP_PAUSE_MS);
        } else {
          setVisible(nextIndex + 1);
          advance(nextIndex);
        }
      }, delay);
    };

    advance(0);
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: containerRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [visible]);

  // Visible steps. Typing dots only show when they're the latest step.
  const allShown = STEPS.slice(0, visible).filter((s, i, arr) => {
    if (s.step.kind === "typing") return i === arr.length - 1;
    return true;
  });

  const finalizeShown = allShown.some((s) => s.step.kind === "finalizeButton");

  // Per-slot tracking for quickAdd: each slot (burger, batata, …) shows
  // only its latest state so the button appears to flip Adicionar → Ok!
  // in place without a stack.
  const quickAddLatestIdx = new Map<string, number>();
  allShown.forEach((s, i) => {
    if (s.step.kind === "quickAdd") {
      quickAddLatestIdx.set(s.step.slot, i);
    }
  });

  return (
    <div
      className="flex flex-col overflow-hidden rounded-2xl bg-background shadow-2xl"
      style={{ border: `1px solid ${ACCENT}25`, height: 540 }}
    >
      {/* Header, matches AiWaiter.tsx real header */}
      <div
        className="flex items-center justify-between px-4 py-3 shrink-0"
        style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}cc)` }}
      >
        <div className="flex items-center gap-3">
          <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/20 shrink-0">
            <Bot className="h-5 w-5 text-white" />
            <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-400 ring-2 ring-white/40" />
          </div>
          <div>
            <p className="font-semibold text-sm text-white leading-tight">Garçom IA</p>
            <p className="text-[11px] text-white/75 leading-tight">Online agora · posso te ajudar</p>
          </div>
        </div>
        <button className="p-1.5 rounded-lg text-white/60" disabled>
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-background scroll-smooth">
        <AnimatePresence initial={false}>
          {allShown.map((s, i) => {
            const step = s.step;

            if (step.kind === "greeting") {
              return (
                <motion.div
                  key={`s-${i}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex gap-2 justify-start"
                >
                  <BotAvatar />
                  <div
                    className="max-w-[82%] rounded-2xl rounded-bl-[4px] px-3.5 py-2.5 text-sm leading-relaxed"
                    style={{ backgroundColor: "hsl(var(--muted) / 0.6)" }}
                  >
                    {step.text}
                  </div>
                </motion.div>
              );
            }

            if (step.kind === "suggestions") {
              return (
                <motion.div
                  key={`s-${i}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="flex flex-wrap gap-1.5 pl-8"
                >
                  {step.chips.map((chip) => (
                    <span
                      key={chip}
                      className="rounded-full border px-3 py-1 text-xs font-medium"
                      style={{ borderColor: `${ACCENT}50`, color: ACCENT }}
                    >
                      {chip}
                    </span>
                  ))}
                </motion.div>
              );
            }

            if (step.kind === "user") {
              return (
                <motion.div
                  key={`s-${i}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex gap-2 justify-end"
                >
                  <div
                    className="max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed text-white"
                    style={{
                      background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}dd)`,
                      borderBottomRightRadius: "4px",
                    }}
                  >
                    {step.text}
                  </div>
                </motion.div>
              );
            }

            if (step.kind === "typing") {
              return (
                <motion.div
                  key={`s-${i}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex gap-2 justify-start"
                >
                  <BotAvatar />
                  <div
                    className="rounded-2xl rounded-bl-[4px] px-3.5 py-2.5"
                    style={{ backgroundColor: "hsl(var(--muted) / 0.6)" }}
                  >
                    <TypingDots />
                  </div>
                </motion.div>
              );
            }

            if (step.kind === "bot") {
              return (
                <motion.div
                  key={`s-${i}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex gap-2 justify-start"
                >
                  <BotAvatar />
                  <div
                    className="max-w-[82%] rounded-2xl rounded-bl-[4px] px-3.5 py-2.5 text-sm leading-relaxed"
                    style={
                      step.highlight
                        ? {
                            backgroundColor: `${ACCENT}10`,
                            borderLeft: `2px solid ${ACCENT}`,
                          }
                        : { backgroundColor: "hsl(var(--muted) / 0.6)" }
                    }
                  >
                    {step.text}
                  </div>
                </motion.div>
              );
            }

            if (step.kind === "quickAdd") {
              if (i !== quickAddLatestIdx.get(step.slot)) return null;
              return (
                <motion.div
                  key={`qa-${step.slot}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.22 }}
                  className="flex gap-2 justify-start"
                >
                  <div className="h-6 w-6 shrink-0" />
                  <div className="max-w-[82%] flex items-center justify-between gap-2 rounded-xl border bg-card px-3 py-2 shadow-sm w-full">
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className="h-8 w-8 rounded-lg shrink-0 flex items-center justify-center"
                        style={{ backgroundColor: `${ACCENT}15` }}
                      >
                        <Sparkles className="h-3.5 w-3.5" style={{ color: ACCENT }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate leading-tight">{step.name}</p>
                        <p className="text-xs font-bold leading-tight" style={{ color: ACCENT }}>
                          {step.price}
                        </p>
                      </div>
                    </div>
                    <motion.button
                      className="shrink-0 h-7 px-2.5 text-xs text-white rounded-lg font-semibold flex items-center gap-1"
                      style={{
                        backgroundColor: step.added ? "#22c55e" : ACCENT,
                      }}
                      animate={step.added ? { scale: [1, 1.08, 1] } : {}}
                      transition={{ duration: 0.3 }}
                      disabled
                    >
                      {step.added ? (
                        <>✓ Ok!</>
                      ) : (
                        <>
                          <ShoppingBag className="h-3 w-3" />
                          Adicionar
                        </>
                      )}
                    </motion.button>
                  </div>
                </motion.div>
              );
            }

            if (step.kind === "confirmation") {
              return (
                <motion.div
                  key={`s-${i}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22 }}
                  className="flex gap-2 justify-start"
                >
                  <BotAvatar />
                  <div
                    className="max-w-[82%] rounded-2xl rounded-bl-[4px] px-3.5 py-2.5 text-sm leading-relaxed flex items-center gap-1.5"
                    style={{ backgroundColor: "#dcfce7", color: "#14532d" }}
                  >
                    <span className="font-semibold">✓ Adicionei:</span>
                    <span className="font-bold">{step.name}</span>
                    <span>·</span>
                    <span className="font-semibold">{step.price}</span>
                    <span>🛒</span>
                  </div>
                </motion.div>
              );
            }

            return null;
          })}
        </AnimatePresence>
      </div>

      {/* Finalize button, slides up from footer, matches real AiWaiter.tsx */}
      <AnimatePresence>
        {finalizeShown && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.3 }}
            className="border-t px-3 pt-3 pb-3 bg-background shrink-0"
          >
            <button
              className="w-full h-11 text-sm font-semibold text-white gap-2 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: ACCENT }}
              disabled
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Finalizar Pedido
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
